/**
 * ファイル操作関連のサービス
 * ブラウザのFile APIを使用したファイル選択・読み込み機能
 */

// import { DatasetId } from '../../types'; // 未使用

/**
 * ファイル選択ダイアログを表示
 * @param accept 受け入れるファイル形式（例: ".csv,.tsv"）
 * @param multiple 複数選択を許可するか
 */
export async function selectFile(
  accept: string = '.csv,.tsv,.mds',
  multiple: boolean = false
): Promise<File | File[] | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.multiple = multiple;

    input.addEventListener('change', () => {
      const files = input.files;
      if (!files || files.length === 0) {
        resolve(null);
        return;
      }

      if (multiple) {
        resolve(Array.from(files));
      } else {
        resolve(files[0]);
      }
    });

    // キャンセル時の処理
    input.addEventListener('cancel', () => {
      resolve(null);
    });

    input.click();
  });
}

/**
 * Fileオブジェクトをテキストとして読み込む
 */
export async function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to read file as text'));
      }
    };
    
    reader.onerror = () => {
      reject(new Error(`Failed to read file: ${reader.error?.message}`));
    };
    
    reader.readAsText(file);
  });
}

/**
 * Fileオブジェクトをバイナリとして読み込む
 */
export async function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to read file as ArrayBuffer'));
      }
    };
    
    reader.onerror = () => {
      reject(new Error(`Failed to read file: ${reader.error?.message}`));
    };
    
    reader.readAsArrayBuffer(file);
  });
}

/**
 * ファイルをダウンロードする
 * @param content ファイル内容
 * @param filename ファイル名
 * @param mimeType MIMEタイプ
 */
export function downloadFile(
  content: string | Blob,
  filename: string,
  mimeType: string = 'text/plain'
): void {
  const blob = content instanceof Blob 
    ? content 
    : new Blob([content], { type: mimeType });
  
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  
  // メモリリークを防ぐためにURLを解放
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

/**
 * ファイルのメタ情報を取得
 */
export function getFileMetadata(file: File): {
  name: string;
  size: number;
  type: string;
  lastModified: Date;
} {
  return {
    name: file.name,
    size: file.size,
    type: file.type || 'unknown',
    lastModified: new Date(file.lastModified),
  };
}

/**
 * ファイル拡張子を取得
 */
export function getFileExtension(filename: string): string {
  const lastDot = filename.lastIndexOf('.');
  if (lastDot === -1) return '';
  return filename.substring(lastDot + 1).toLowerCase();
}

/**
 * ディレクトリ選択（File System Access API使用）
 * 注意: Chrome/Edge等の対応ブラウザでのみ動作
 */
export async function selectDirectory(): Promise<FileSystemDirectoryHandle | null> {
  if (!('showDirectoryPicker' in window)) {
    console.warn('File System Access API is not supported in this browser');
    return null;
  }

  try {
    const dirHandle = await window.showDirectoryPicker!();
    return dirHandle as FileSystemDirectoryHandle;
  } catch (err) {
    // ユーザーがキャンセルした場合
    if ((err as Error).name === 'AbortError') {
      return null;
    }
    throw err;
  }
}